/**
 * ClusterLayout — the plumbing for the Feb 2026 course-page 5-cluster
 * restructure.
 *
 * Two components + a provider let us re-group a giant existing page
 * into 5 accordions WITHOUT physically moving any JSX blocks:
 *
 *   <ClusterProvider>
 *     <div className="flex flex-col">
 *       <ClusterHeader cluster={1} title="Essential info" ... />
 *       <ClusterMember cluster={1}><SectionIdentity /></ClusterMember>
 *       <ClusterMember cluster={1}><SectionCurriculum /></ClusterMember>
 *       <ClusterHeader cluster={2} title="Applying & getting in" ... />
 *       <ClusterMember cluster={2}><SectionEntry /></ClusterMember>
 *       …
 *     </div>
 *   </ClusterProvider>
 *
 * How it works:
 *   1. `ClusterMember cluster={N}` renders `<div className="order-[N*10]">`.
 *      CSS flex `order` re-shuffles siblings visually — the DOM stays
 *      in source order, but the browser paints them in cluster order.
 *   2. `ClusterHeader cluster={N}` renders at `order-[N*10 - 1]` so it
 *      always paints immediately before its members.
 *   3. The header toggles `openClusters[N]` in context; members read
 *      that state and hide themselves via `display: none` when closed.
 *
 * Tailwind can't compute arbitrary `order-*` at runtime, so we use
 * `style={{ order: … }}` for the numeric ordering. Simple, reliable,
 * no build-time class purge headaches.
 */
import { createContext, useContext, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

const ClusterCtx = createContext({
  open: {},
  toggle: () => {},
  register: () => {},
});

/**
 * Wrap the page body in a ClusterProvider before rendering any
 * ClusterHeader / ClusterMember. Default open state = all open.
 */
export function ClusterProvider({ initialOpen = {}, children }) {
  const [open, setOpen] = useState(initialOpen);
  const value = useMemo(
    () => ({
      open,
      toggle: (n) =>
        setOpen((s) => ({ ...s, [n]: s[n] === undefined ? false : !s[n] })),
      isOpen: (n) => (open[n] === undefined ? true : open[n]),
    }),
    [open],
  );
  return <ClusterCtx.Provider value={value}>{children}</ClusterCtx.Provider>;
}

export function useCluster() {
  return useContext(ClusterCtx);
}

/**
 * ClusterHeader — the accordion trigger for a cluster. Always paints
 * immediately before its members (order = N*10 - 1).
 */
export function ClusterHeader({
  cluster,
  eyebrow,
  title,
  subtitle,
  topicsFilled = 0,
  topicsTotal = 0,
  id,
}) {
  const { toggle, isOpen } = useCluster();
  const open = isOpen(cluster);
  const label = String(cluster).padStart(2, "0");
  const fillRatio = topicsTotal > 0 ? topicsFilled / topicsTotal : 0;
  const pillTone =
    fillRatio === 0
      ? "bg-stone-100 text-stone-500 border-stone-200"
      : fillRatio < 0.5
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-emerald-50 text-emerald-800 border-emerald-200";

  return (
    <div
      id={id}
      data-testid={`cluster-header-${cluster}`}
      style={{ order: cluster * 10 - 1 }}
      className="scroll-mt-28 mt-24 pt-14 pb-2 border-t-2 border-stone-900"
    >
      <button
        type="button"
        onClick={() => toggle(cluster)}
        aria-expanded={open}
        data-testid={`cluster-toggle-${cluster}`}
        className="w-full flex items-start gap-5 text-left group"
      >
        <span
          aria-hidden
          className="flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-stone-900 text-stone-900 font-editorial text-2xl tracking-tight bg-white group-hover:bg-stone-900 group-hover:text-white transition-colors"
        >
          {label}
        </span>
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className="text-[12px] font-underground-sc tracking-[0.15em] text-stone-500 mb-1">
              {eyebrow}
            </div>
          )}
          <h2 className="font-editorial text-3xl sm:text-4xl text-stone-900 leading-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-sm text-stone-600 max-w-prose">{subtitle}</p>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-3">
          {topicsTotal > 0 && (
            <span
              data-testid={`cluster-pill-${cluster}`}
              className={`hidden sm:inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-underground-sc tracking-wider ${pillTone}`}
            >
              {topicsFilled} of {topicsTotal} covered
            </span>
          )}
          <ChevronDown
            className={`w-5 h-5 text-stone-500 transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
    </div>
  );
}

/**
 * ClusterMember — wraps any content that belongs to `cluster`. Sets
 * CSS `order` so all members of the same cluster paint together, and
 * hides when the cluster is collapsed.
 */
export function ClusterMember({ cluster, children }) {
  const { isOpen } = useCluster();
  const open = isOpen(cluster);
  return (
    <div
      data-cluster={cluster}
      style={{ order: cluster * 10, display: open ? undefined : "none" }}
      className="mb-4"
    >
      {children}
    </div>
  );
}
